import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../security/security/AuthContext';
import axios from 'axios';
import Message from './loaders/Message';
import RegisterLoading from './loaders/RegisterLoading';
import LoadingLogin from './loaders/LoadingLogin';
import { checkUserStatus } from '../../security/api/ApiClient';
import HomeToastNotification from './loaders/HomeToastNotification';
import WhatsAppFloat from './WhatsAppFloat';

const Auth = () => {
  const [isLogin, setIsLogin] = useState(false);
  const [form, setForm] = useState({
    fullName: '',
    email: '',
    mobile: '',
    password: '',
    role: 'USER'
  });
  
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [invitationCode, setInvitationCode] = useState('');
  const [toastNotification, setToastNotification] = useState(null);
  
  const navigate = useNavigate();
  const location = useLocation();
  const authContext = useAuth();

  // Your existing useEffect for invitation code
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const code = params.get('invite_code');
    if (code) {
      setInvitationCode(code);
    }
  }, [location]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const downloadApp = () => {
    setToastNotification({ message: 'Mobile App will be available Soon.', type: 'success' });
  };

 const validateSignup = () => {
  const errors = {};
  if (!form.fullName?.trim()) errors.fullName = 'Full Name is required';
  if (!form.email) {
    errors.email = 'Email Address is required';
  } else if (!/\S+@\S+\.\S+/.test(form.email)) {
    errors.email = 'Email Address is invalid';
  }
  if (!form.mobile) {
    errors.mobile = 'Mobile Number is required';
  } else if (!/^(07|01|254)\d+$/.test(form.mobile)) {
    errors.mobile = 'Phone number must start with 07, 01, or 254';
  }
  if (!form.password) {
    errors.password = 'Password is required';
  } else if (form.password.length < 6) {
    errors.password = 'Password must be at least 6 characters';
  }
  return errors;
};

  const validateLogin = () => {
    const errors = {};
    if (!form.email) {
      errors.email = 'Email Address is required';
    } else if (!/\S+@\S+\.\S+/.test(form.email)) {
      errors.email = 'Email Address is invalid';
    }
    if (!form.password) {
      errors.password = 'Password is required';
    }
    return errors;
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    const validationErrors = validateSignup();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    setMessage('');
    setMessageType('');

    try {
      const response = await axios.post('https://api.visiontech2030.com/register', form, {
        params: { invitationCode },
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.status === 200) {
        setMessage('Registration successful. Thank You For Registering with Us. You can now login to your account!');
        setMessageType('success');
        setIsLogin(true);
        setForm({
          ...form,
          fullName: '',
          mobile: ''
        });
      }
    } catch (error) {
      if (error.response?.data?.error) {
        setMessage(error.response.data.error);
      } else {
        setMessage('An error occurred. Please try again.');
      }
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    const validationErrors = validateLogin();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    try {
      const result = await authContext.login(form.email, form.password);
      if (result.success) {
        const userStatus = await checkUserStatus();
        
        switch(userStatus) {
          case 'ACTIVE':
          case 'INACTIVE':
            setMessage('Login successful!');
            setMessageType('success');
            setTimeout(() => {
              navigate('/home');
            }, 2000);
            break;
          case 'SUSPENDED':
            setMessage('Your account has been suspended. Please contact the administrator.');
            setMessageType('error');
            authContext.logout();
            break;
          default:
            setMessage('Invalid account status. Please contact support.');
            setMessageType('error');
            authContext.logout();
        }
      } else {
        setMessage(result.message);
        setMessageType('error');
      }
    } catch (error) {
      setMessage('An unexpected error occurred');
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  const toggleForm = () => {
    setIsLogin(!isLogin);
    setForm({
      fullName: '',
      email: '',
      mobile: '',
      password: '',
      role: 'USER'
    });
    setErrors({});
    setMessage('');
  };

  return (
    <div className="vision-login-signup-improvements">
      <div className="vision-login-signup-background">
        <div className="vision-login-signup-coins vision-login-signup-coin1"></div>
        <div className="vision-login-signup-coins vision-login-signup-coin2"></div>
        <div className="vision-login-signup-coins vision-login-signup-coin3"></div>
        <div className="vision-login-signup-coins vision-login-signup-coin4"></div>
        <div className="vision-login-signup-coins vision-login-signup-coin5"></div>
        <div className="vision-login-signup-coins vision-login-signup-coin6"></div>
        <div className="vision-login-signup-glowing-overlay"></div>
      </div>
      
      <div className="vision-login-signup-content">
        <div className="vision-login-signup-logo-container">
          <h1 className="vision-login-signup-title">VISIONTECH 2030 CAPITAL</h1>
          <div className="vision-login-signup-subtitle">Investing in Tomorrow, Today</div>
        </div>
        
        {loading && (isLogin ? <LoadingLogin /> : <RegisterLoading />)}
        
        <div className="vision-login-signup-card">
          <div className="vision-login-signup-decoration-circle vision-login-signup-decoration-1"></div>
          <div className="vision-login-signup-decoration-circle vision-login-signup-decoration-2"></div>
          
          <div className="vision-login-signup-tabs">
            <button 
              className={`vision-login-signup-tab ${isLogin ? 'vision-login-signup-active' : ''}`}
              onClick={() => setIsLogin(true)}
            >
              <span className="vision-login-signup-tab-icon login-icon"></span>
              Login
            </button>
            <button 
              className={`vision-login-signup-tab ${!isLogin ? 'vision-login-signup-active' : ''}`}
              onClick={() => setIsLogin(false)}
            >
              <span className="vision-login-signup-tab-icon signup-icon"></span>
              Sign Up
            </button>
          </div>

          <div className="vision-login-signup-form-container">
            {!isLogin && (
              <div className="vision-login-signup-incentive-banner">
                <div className="vision-login-signup-incentive-content">
                  <div className="vision-login-signup-incentive-icon"></div>
                  <p className="vision-login-signup-incentive-text">
                    Register now and earn <span className="vision-login-signup-incentive-highlight">100+ free cash</span> instantly!
                  </p>
                </div>
              </div>
            )}
            
            {message && <Message type={messageType} message={message} />}
            
            <form onSubmit={isLogin ? handleLogin : handleSignup} className="vision-login-signup-form">
              {!isLogin && (
                <div className="vision-login-signup-input-group">
                  <label htmlFor="fullName" className="vision-login-signup-label">Full Name</label>
                  <div className="vision-login-signup-input-container">
                    <span className="vision-login-signup-input-icon fullname-icon"></span>
                    <input
                      type="text"
                      id="fullName"
                      name="fullName"
                      className="vision-login-signup-input"
                      placeholder="Your Full Name"
                      value={form.fullName}
                      onChange={handleChange}
                    />
                  </div>
                  {errors.fullName && <p className="vision-login-signup-error">{errors.fullName}</p>}
                </div>
              )}

              <div className="vision-login-signup-input-group">
                <label htmlFor="email" className="vision-login-signup-label">Email Address</label>
                <div className="vision-login-signup-input-container">
                  <span className="vision-login-signup-input-icon email-icon"></span>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    className="vision-login-signup-input"
                    placeholder="Your Email Address"
                    value={form.email}
                    onChange={handleChange}
                  />
                </div>
                {errors.email && <p className="vision-login-signup-error">{errors.email}</p>}
              </div>

              {!isLogin && (
                <div className="vision-login-signup-input-group">
                  <label htmlFor="mobile" className="vision-login-signup-label">Phone Number</label>
                  <div className="vision-login-signup-input-container">
                    <span className="vision-login-signup-input-icon phone-icon"></span>
                    <input
                      type="number"
                      id="mobile"
                      name="mobile"
                      className="vision-login-signup-input"
                      placeholder="Your Phone Number"
                      value={form.mobile}
                      onChange={handleChange}
                    />
                  </div>
                  {errors.mobile && <p className="vision-login-signup-error">{errors.mobile}</p>}
                </div>
              )}

              <div className="vision-login-signup-input-group">
                <label htmlFor="password" className="vision-login-signup-label">Password</label>
                <div className="vision-login-signup-input-container">
                  <span className="vision-login-signup-input-icon password-icon"></span>
                  <input
                    type="password"
                    id="password"
                    name="password"
                    className="vision-login-signup-input"
                    placeholder="Your Password"
                    value={form.password}
                    onChange={handleChange}
                  />
                </div>
                {errors.password && <p className="vision-login-signup-error">{errors.password}</p>}
              </div>
              
              <button type="submit" className="vision-login-signup-submit">
                <span className="vision-login-signup-submit-text">{isLogin ? 'Login' : 'Create Account'}</span>
                <span className="vision-login-signup-submit-arrow"></span>
              </button>

              {isLogin && (
                <button 
                  type="button" 
                  className="vision-login-signup-forgot-password"
                  onClick={() => navigate('/forgot-password')}
                >
                  Forgot Password?
                </button>
              )}
              
              <div className="vision-login-signup-footer">
                <p className="vision-login-signup-toggle-text">
                  {isLogin ? "Don't have an account?" : "Already have an account?"}
                  <button 
                    type="button"
                    className="vision-login-signup-toggle-button"
                    onClick={toggleForm}
                  >
                    {isLogin ? 'Sign Up' : 'Login'}
                  </button>
                </p>
              </div>
            </form>
          </div>
        </div>
        
        <div className="vision-login-signup-features">
          <div className="vision-login-signup-feature">
            <div className="vision-login-signup-feature-icon secure-icon"></div>
            <h3 className="vision-login-signup-feature-title">Secure Investments</h3>
          </div>
          <div className="vision-login-signup-feature">
            <div className="vision-login-signup-feature-icon returns-icon"></div>
            <h3 className="vision-login-signup-feature-title">High Returns</h3>
          </div>
          <div className="vision-login-signup-feature">
            <div className="vision-login-signup-feature-icon support-icon"></div>
            <h3 className="vision-login-signup-feature-title">24/7 Support</h3>
          </div>
        </div>
      </div>
      
      {toastNotification && (
        <HomeToastNotification
          message={toastNotification.message}
          type={toastNotification.type}
          onClose={() => setToastNotification(null)}
        />
      )}
      <WhatsAppFloat />
    </div>
  );
};

export default Auth;